import {
  AutocompleteInput,
  Confirm,
  Create,
  FormDataConsumer,
  NumberInput,
  RadioButtonGroupInput,
  ReferenceInput,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  minValue,
  required,
  useGetList,
  useNotify,
  useRefresh
} from 'react-admin';
import DateTimeInputUTC from '../../components/DateTimeInputUTC';
import { useSaveContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { CheckChainUsesValidation, LazyLoadAutoCompleteInput, PriceInput, ValidationRuleKey, Wrapper } from '../../components';
import { CPFInput } from '../../components/Inputs';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import { getChainId, getPlaceId } from '../../lib';
import { Alert, Box } from '@mui/material';
import { filterToQuery, formatAttendantLabel, formatNumber, parseCurrency } from '../../utils';
import { useState } from 'react';
import { track, EVENTS } from '../../lib/analytics';
import { applySaveErrors } from '../../components/SaveConfirmButton/applySaveErrors';

const HoseInput = () => (
  <FormDataConsumer>
    {({ formData }) => {

      if (!formData.placeId || !formData.fuelId) { return null; }

      const { data: hoseNumber, isLoading: isPendingHose, isError } = useGetList(`stations/${formData.placeId}/hose-numbers`, { filter: { fuelId: formData.fuelId } });

      if (!isPendingHose && isError) { return null; }

      return (
        <CheckChainUsesValidation ruleKey={ValidationRuleKey.requiredHoseNumber}>
          <RadioButtonGroupInput
            source="hoseNumber"
            optionText="hoseNumber"
            label="Bico"
            choices={hoseNumber || []}
            isLoading={isPendingHose}
          />
        </CheckChainUsesValidation>
      );
    }}
  </FormDataConsumer>
);

const PointsCreateToolbar = () => {

  const [confirmDialog, setConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const form = useFormContext();
  const saveContext = useSaveContext();

  const verifySubmit = async (value) => {
    if (isSubmitting) return;

    if (value.fillinAmount > 0) {
      const pricePerLiter = +(value.fillinValue / value.fillinAmount).toFixed(3);
      if (pricePerLiter < 1 || pricePerLiter > 10) {
        setConfirmDialog(true);
        return;
      }
    }
    form.handleSubmit(handleSubmit)();
  }

  const handleSubmit = async (value) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const errors = saveContext.save && await saveContext.save(value, {
        onSuccess: () => {
          form.reset();
          window.scrollTo(0, 0);
          track(EVENTS.POINTS_ADDED_MANUAL, {
            place_id: value?.placeId ?? 'unknown',
            fillin_amount: Number(value?.fillinAmount) || 0,
            has_attendant: !!value?.attendantId,
          });
          notify('ra.notification.created', {
            type: 'info',
            messageArgs: { smart_count: 1 },
          });
          refresh();
        },
      });
      applySaveErrors(errors, form.setError, notify);
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleConfirmDialog = () => {
    setConfirmDialog(false);
    form.handleSubmit(handleSubmit)();
  }

  const handleCloseDialog = () => {
    setConfirmDialog(false);
  }

  return (
    <Toolbar>
      <FormDataConsumer>
        {({ formData }) => (
          <>
            <SaveButton
              type="button"
              label="ra.action.save"
              variant="text"
              disabled={isSubmitting}
              onClick={form.handleSubmit(verifySubmit)}
            />
            <Confirm
              isOpen={confirmDialog}
              title="Atenção"
              content={
                <Alert
                  variant="outlined"
                  severity="warning"
                >
                  O preço por litro é <strong>{formatNumber(+(formData.fillinValue / formData.fillinAmount).toFixed(3), { style: 'currency', currency: 'BRL', minimumFractionDigits: 3, maximumFractionDigits: 3 })}</strong> <br />
                  Este valor parece incorreto. Tem certeza que deseja continuar?
                </Alert>
              }
              onConfirm={handleConfirmDialog}
              onClose={handleCloseDialog}
            />
          </>
        )}
      </FormDataConsumer>
    </Toolbar>
  );
};

const sanitize = (data) => {
  return { ...Object.fromEntries(Object.entries(data).filter(([key, value]) => value !== null)) };
};

export default props => {

  return (
    <Wrapper>
      <Permission permission={PERMISSIONS.ADD_POINTS}>
        <Create {...props} title="Adicionar Pontos" resource={`chains/${getChainId()}/addition-points`} transform={sanitize}>
          <SimpleForm sanitizeEmptyValues toolbar={<PointsCreateToolbar />} defaultValues={{ placeId: getPlaceId() }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <NumberInput
                source="couponInvoice"
                label="Cupom Fiscal"
                validate={[required("Cupom Fiscal é obrigatório"), minValue(1)]}
                min={1}
                sx={{ maxWidth: 200 }}
              />
              <TextInput
                source="couponSerialNumber"
                label="Número de Série"
                validate={required("Número de Série é obrigatório")}
                sx={{ maxWidth: 200 }}
              />
            </Box>
            <CPFInput source="customerCpf" label="CPF" isRequired sx={{ maxWidth: 250 }} />
            {!getPlaceId() && (
              <ReferenceInput
                source="placeId"
                filter={{ chainId: getChainId() }}
                perPage={9999}
                reference="stations"
                sort={{ field: 'name', order: 'ASC' }}
              >
                <AutocompleteInput
                  filterToQuery={filterToQuery}
                  label="Posto"
                  optionText="name"
                  validate={required("Posto é obrigatório")}
                  sx={{ maxWidth: 300 }}
                />
              </ReferenceInput>
            )}
            <FormDataConsumer>
              {({ formData }) => formData.placeId &&
                //  @ts-ignore
                <LazyLoadAutoCompleteInput
                  label="Frentista"
                  source="attendantId"
                  format={record => formatAttendantLabel(record)}
                  style={{ minWidth: 205, maxWidth: 300 }}
                  reference="attendants"
                  filter={{ placeId: formData.placeId }}
                />
              }
            </FormDataConsumer>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextInput
                source="fillinAmount"
                label="Litros"
                defaultValue="0"
                format={(value) => {
                  if (!value || value === 0) {
                    return "0,00";
                  }
                  return `${Number(value).toFixed(2)}`;
                }}
                parse={(value) => {
                  if (!value || value === '') {
                    return 0;
                  }
                  return parseCurrency(value, 2);
                }}
                validate={[required("Litros é obrigatório"), minValue(1)]}
                sx={{ maxWidth: 150 }}
              />
              <PriceInput source="fillinValue" label="Valor" precision={2} validate={[required("Valor é obrigatório"), minValue(0.01)]} sx={{ maxWidth: 150 }} />
            </Box>
            <DateTimeInputUTC
              source="createdAt"
              label="Data e Hora"
              sx={{ maxWidth: 250 }}
            />
            <FormDataConsumer>
              {({ formData }) => formData.placeId && (
                <ReferenceInput
                  source="fuelId"
                  filter={{ placeId: formData.placeId, hasFactor: true }}
                  reference="fuels"
                  sort={{ field: 'name', order: 'ASC' }}
                >
                  <SelectInput
                    validate={required("Combustível é obrigatório")}
                    label="Combustível"
                    optionText="name"
                    sx={{ maxWidth: 250 }}
                  />
                </ReferenceInput>
              )}
            </FormDataConsumer>
            <HoseInput />
            <CheckChainUsesValidation ruleKey={ValidationRuleKey.requiredEncerrante}>
              <NumberInput
                source="encerrante"
                label="Encerrante"
                sx={{ maxWidth: 200 }}
              />
            </CheckChainUsesValidation>
            <TextInput source="description" label="Observação" multiline sx={{ maxWidth: 400 }} />
          </SimpleForm>
        </Create>
      </Permission>
    </Wrapper>
  )
}
